// Pure helpers for showing a recording's connected hike on the recording map.
// Kept free of Mapbox/DOM so Jest can exercise them directly.

/** Which of the two the sidebar is showing: the recording itself, or the hike it was recorded on. */
export type RecordingSidebarTab = 'recording' | 'hike';

/** Matches the `activeView` shape HikeTrailsPanel emits on `view-changed`. */
export type HikeActiveView =
  | { type: 'full' }
  | { type: 'day'; dayNumber: number }
  | { type: 'route'; trailId: string | number };

type Position = number[];
interface Feature {
  type?: string;
  geometry?: { type: string; coordinates: any } | null;
  properties?: Record<string, any> | null;
}

/**
 * The selected tab's layer is always drawn; the sidebar's "Show … on map" switch adds the
 * other one. With no hike to show, only the recording is drawn whatever the switch says.
 */
export function layersVisibleFor(
  tab: RecordingSidebarTab,
  showOther: boolean,
  hasHike = true,
): { recording: boolean; hike: boolean } {
  if (!hasHike) return { recording: true, hike: false };
  return {
    recording: tab === 'recording' || showOther,
    hike: tab === 'hike' || showOther,
  };
}

/** localStorage key for the sidebar's "Show … on map" switch, shared by every recording. */
export const SHOW_OTHER_STORAGE_KEY = 'recordingMap.showOtherOnMap';

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem'>;

function defaultStorage(): KeyValueStorage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

/**
 * The viewer's last choice for the "Show … on map" switch. Off until they turn it on, and
 * off whenever storage is unavailable or holds anything unexpected.
 */
export function readShowOtherPreference(storage: KeyValueStorage | null = defaultStorage()): boolean {
  try {
    return storage?.getItem(SHOW_OTHER_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Remembers the switch for the next recording that has a hike. Storage failures are ignored. */
export function writeShowOtherPreference(
  value: boolean,
  storage: KeyValueStorage | null = defaultStorage(),
): void {
  try {
    storage?.setItem(SHOW_OTHER_STORAGE_KEY, value ? 'true' : 'false');
  } catch {
    // Private mode / blocked storage: the switch still works for this page view.
  }
}

function isRouteLine(feature: Feature): boolean {
  return feature?.geometry?.type === 'MultiLineString';
}

function inActiveView(feature: Feature, view: HikeActiveView): boolean {
  if (view.type === 'day') return feature.properties?.hikeDayNumber === view.dayNumber;
  if (view.type === 'route') {
    const id = feature.properties?.ptv2PlannedTrailId;
    return id != null && String(id) === String(view.trailId);
  }
  return true;
}

/**
 * Splits the hike's route lines into the ones the selected day/route covers (drawn at full
 * strength) and the rest (dimmed), as the hike map does. Start/end points in the hike
 * GeoJSON are not lines and are left out.
 */
export function splitHikeLines(
  features: Feature[] | null | undefined,
  view: HikeActiveView,
): { active: Feature[]; inactive: Feature[] } {
  const active: Feature[] = [];
  const inactive: Feature[] = [];
  (features || []).filter(isRouteLine).forEach((feature) => {
    (inActiveView(feature, view) ? active : inactive).push(feature);
  });
  return { active, inactive };
}

function eachPosition(coordinates: any, visit: (p: Position) => void): void {
  if (!Array.isArray(coordinates)) return;
  if (typeof coordinates[0] === 'number') {
    visit(coordinates as Position);
    return;
  }
  coordinates.forEach((child) => eachPosition(child, visit));
}

/**
 * The [[west, south], [east, north]] box around every coordinate in `features`, or null
 * when there is nothing to frame. Accepts any geometry type.
 */
export function boundsOfFeatures(features: Feature[] | null | undefined): [Position, Position] | null {
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  (features || []).forEach((feature) => {
    eachPosition(feature?.geometry?.coordinates, ([lng, lat]) => {
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) return;
      west = Math.min(west, lng);
      south = Math.min(south, lat);
      east = Math.max(east, lng);
      north = Math.max(north, lat);
    });
  });
  if (!Number.isFinite(west)) return null;
  return [[west, south], [east, north]];
}

/**
 * What the map should frame: the recording on its tab, and the selected part of the hike on
 * the hike's tab (falling back to the recording until the hike has lines). The other layer
 * can still be switched on, but the selected tab decides the framing.
 */
export function boundsForTab(
  tab: RecordingSidebarTab,
  recordingFeatures: Feature[] | null | undefined,
  hikeFeatures: Feature[] | null | undefined,
  hikeView: HikeActiveView,
): [Position, Position] | null {
  const recording = boundsOfFeatures(recordingFeatures);
  if (tab === 'recording') return recording;
  return boundsOfFeatures(splitHikeLines(hikeFeatures, hikeView).active) || recording;
}
